import type { BasicShellInfo } from '@/features/pwIncubation/queries';

import tw, { styled } from 'twin.macro';
import { Avatar, Button, CircularProgress, CircularProgressLabel, Tooltip } from '@chakra-ui/react';
import Link from 'next/link';
import { useContainerQuery } from 'react-container-query';
import { atom, useAtomValue } from 'jotai';
import { atomWithQuery } from 'jotai/query';
import { waitForAll } from 'jotai/utils';
import * as R from 'ramda';

import { subqlEndpointAtom } from '@/atoms/subqlEndpointAtom';
import formatNumber from '@/utils/formatNumber';
import { spiritAtom } from '@/features/spirit/atoms/spiritAtom';
import { currentAccountAtom, currentProfileAtom } from '@/features/identity/atoms';
import { canStartIncubationAtom } from '@/features/pwIncubation/atom';
import { queryFedCountsByReceiver } from '@/features/pwIncubation/queries';
import { queryEarnedEnergyPoints } from '@/features/spirit/queries';
import { trpcJotai } from '@/providers/trpc';

import RaceAvatar from '../RaceAvatar/RaceRavatar';
import NoticeIcon from '../../svgs/notice.svg';
import FoodIcon from '../../svgs/food-default.svg';
import FoodChampionIcon from '../../svgs/food-champion.svg';
import PowerMeterIcon from '../../svgs/power-meter.svg';
import QuestionIcon from '../../svgs/question2.svg';

//
// Atoms
//

interface UserInfo {
  displayName: string
  spiritNftId: number | null
  rank?: number
  totalInRank?: number
  level?: number
  progress?: number
  expToNextLevel?: number
  power: number
}

const currentUserEarnedEnergyPointsAtom = atomWithQuery(get => {
  const subqlEndpoint = get(subqlEndpointAtom)
  const currentAccount = get(currentAccountAtom)
  return queryEarnedEnergyPoints(subqlEndpoint, currentAccount?.address)
})

const userInfoAtom = atom(get => {
  const profile = get(currentProfileAtom)
  const spirit = get(spiritAtom)
  const points = get(currentUserEarnedEnergyPointsAtom)
  const userInfo: UserInfo = { displayName: '', spiritNftId: null, power: 0 }
  userInfo.displayName = profile.displayName
  userInfo.spiritNftId = spirit.formattedNftId
  userInfo.rank = spirit.rank
  userInfo.totalInRank = spirit.totalInRank
  userInfo.level = spirit.reputation.level
  userInfo.progress = spirit.reputation.progress
  userInfo.expToNextLevel = spirit.reputation.target - spirit.reputation.exp
  userInfo.power = spirit.power + points
  return userInfo
})

const receivedFoodsAtom = atomWithQuery(get => {
  const endpoint = get(subqlEndpointAtom)
  const account = get(currentAccountAtom)
  return queryFedCountsByReceiver(endpoint, account?.address)
})


const shellFedRankForOwnerAtom = trpcJotai.campaigns.shellFedRankForOwner.atomWithQuery(get => {
  const account = get(currentAccountAtom)
  return account?.address || ''
})

const higestRankAtom = atom(async get => R.head<BasicShellInfo>(get(shellFedRankForOwnerAtom)))

const incubationInfoAtom = atom(get => {
  const receivedFoods = get(receivedFoodsAtom)
  const shell = get(higestRankAtom)
  return { receivedFoods, shell }
})

//
// Views
//

const queryContainer = {
  minimal: {
    maxWidth: 1080
  },
  compact: {
    minWidth: 425,
    maxWidth: 1080
  },
  full: {
    minWidth: 1080
  }
};

type VariantLiterval = 'minimal' | 'full' | 'compact';

const UserName = ({
  displayName,
  spiritId,
  variant
}: { displayName: string; spiritId: number | null; variant: VariantLiterval }) => {
  return (
    <div tw='flex flex-row items-center gap-3'>
      {variant === 'full' && <Avatar src='/static/spirit/spirit.png' w='52px' h='52px' />}
      <div>
        <h4
          css={[
            tw`text-base font-semibold`,
            variant === 'minimal' ? tw`w-full break-all` : tw`w-32 whitespace-nowrap overflow-hidden text-ellipsis`
          ]}
        >
          {displayName}
        </h4>
        {!!spiritId ? (
          <sub tw='text-sm'>#{spiritId}</sub>
        ) : null}
      </div>
    </div>
  );
};

const OverrideCircularProgress = styled(CircularProgress)`
  .chakra-progress__track {
    stroke: #1a4d46 !important;
  }
`;

interface SpiritLevelProps {
  level: number;
  progress: number;
  expToNextLevel: number;
  variant: VariantLiterval;
}

const SpiritLevel = ({ level, progress, expToNextLevel, variant }: SpiritLevelProps) => {
  return (
    <div tw="flex flex-row items-center gap-3 text-sm">
      {variant === 'full' && (
        <div>
          <div>To next level</div>
          <div>
            <strong tw='text-brand font-semibold'>{formatNumber(expToNextLevel)}</strong> EXP
          </div>
        </div>
      )}
      <OverrideCircularProgress
        size='12'
        value={progress}
        thickness='8px'
        color='brand.500'
        bg='#012321'
        rounded='full'
      >
        <CircularProgressLabel>
          <div tw="flex flex-row justify-center items-center text-white text-lg">{level}</div>
        </CircularProgressLabel>
      </OverrideCircularProgress>
    </div>
  );
};

interface SpiritRankingProps {
  total: number;
  ranked: number;
  variant: VariantLiterval;
}

const SpiritRanking = ({ total, ranked, variant }: SpiritRankingProps) => {
  return (
    <div tw='flex flex-row items-center gap-3.5'>
      {variant === 'full' && <img tw='object-contain w-20' src='/static/spirit/ranking-crown.png' alt='' />}
      <div tw='flex flex-col gap-1'>
        <div tw='text-sm'>World Ranking</div>
        <div tw='inline-flex gap-2 items-center'>
          <span tw='text-brand text-base font-semibold'>
            {formatNumber(ranked)}
            <span tw='px-0.5'>/</span>
            {formatNumber(total)}
          </span>
          <Link
            href='https://docs.phala.world/introduction/the-spirit-nft/spirit-world-ranks'
            passHref
            target='_blank'
            rel='noopener'>
            <NoticeIcon tw='h-3.5 w-3.5 text-brand' />
          </Link>
        </div>
      </div>
    </div>
  );
};

const IncubationReceivedFoods = ({ foods, variant }: { foods: number; variant: VariantLiterval }) => {
  return (
    <div tw='flex flex-row items-center gap-2.5 relative'>
      {variant === 'full' && <FoodIcon tw='w-9 h-9 text-brand' />}
      <div tw='flex flex-col gap-2 text-sm leading-none'>
        <div>Received</div>
        <div>
          <strong tw='font-semibold text-lg text-brand leading-none'>{foods}</strong> Foods
        </div>
      </div>
      <Tooltip hasArrow label='The amount of times every shell this account owns has been fed.'>
        <span><QuestionIcon tw='h-3 w-3 text-brand' /></span>
      </Tooltip>
    </div>
  );
};

const FoodRanking = ({ shell, variant }: { shell?: BasicShellInfo | null; variant: VariantLiterval }) => {
  if (!shell) {
    return null
  }
  return (
    (<Link
      href='/leaderboards/food'
      passHref
      tw='flex flex-row gap-2.5 items-center'>
      {variant === 'full' && <FoodChampionIcon tw='h-8 w-8 text-brand' />}
      <div tw='flex flex-row items-center gap-1.5 rounded-3xl bg-coolgary-950'>
        <RaceAvatar size='40px' race={shell.race} rarity={shell.rarity} />
        <span tw='min-w-[2rem] inline-flex justify-center pr-4 font-secondary text-brand'>{shell.rank}</span>
      </div>
    </Link>)
  );
};

const PowerMeter = ({ power, variant }: { power: number; variant: VariantLiterval }) => {
  return (
    <div tw='flex flex-row gap-5 items-center'>
      <div tw='relative'>
        <PowerMeterIcon tw='w-14 h-14 text-brand' />
        <div tw='absolute top-0 left-0 w-full h-full flex flex-col justify-center items-center'>
          <span tw='font-secondary font-bold text-base text-brand'>{power}</span>
        </div>
      </div>
      {variant === 'full' && (
        <Link
          href='https://docs.phala.world/introduction/the-shell-nft/incubation-and-customization'
          passHref
          legacyBehavior>
          <Button as='a' variant='secondary' size='sm' target='_blank' rel='noopener noreferrer'>
            Get more
          </Button>
        </Link>
      )}
    </div>
  );
};

const IncubationBarContainer = styled.section`
  ${tw`flex flex-row flex-wrap gap-3 justify-between px-8 py-3 rounded-sm bg-[rgb(22, 27, 31)]`}
  backdrop-filter: blur(10px);
`;

export default function IncubationUserBar() {
  const [params, ref] = useContainerQuery(queryContainer, { width: 800 });
  const variant = params.minimal ? 'minimal' : 'full';

  const [currentAccount, userInfo, incubationInfo, canStartIncubation] = useAtomValue(waitForAll([
    currentAccountAtom,
    userInfoAtom,
    incubationInfoAtom,
    canStartIncubationAtom,
  ]))

  if (!currentAccount || userInfo.spiritNftId === null) {
    return null
  }
  return (
    <IncubationBarContainer ref={ref}>
      <UserName displayName={userInfo.displayName} spiritId={userInfo.spiritNftId} variant={params.compact ? 'compact' : variant} />
      {!!(userInfo.level && userInfo.expToNextLevel) ? (
        <SpiritLevel level={userInfo.level} progress={userInfo.progress || 0} expToNextLevel={userInfo.expToNextLevel} variant={variant} />
      ) : null}
      {!!(userInfo.rank && userInfo.totalInRank) ? (
        <SpiritRanking total={userInfo.totalInRank} ranked={userInfo.rank} variant={variant} />
      ): null}
      <IncubationReceivedFoods foods={incubationInfo.receivedFoods} variant={variant} />
      <FoodRanking shell={incubationInfo.shell} variant={variant} />
      <PowerMeter power={userInfo.power} variant={variant} />
    </IncubationBarContainer>
  );
}
